import { useEffect, useId, useState } from 'react'
import { nextTab } from '../tabs'
import { fetchCameras, fetchSiteMap, imageUrl, type SiteMap } from '../api'
import type { Cluster, Site } from '../types'
import type { SiteHealth } from '../useCameraHealth'
import type { SiteTamper } from '../siteIssues'
import { isInProgress } from '../activity'
import { useFinishTick } from '../useFinishTick'
import { relTime } from '../relTime'
import { describe, nameOf, type CameraState } from '../siteMap'
import { SitePlan } from './SitePlan'
import { ZoomControls } from './ZoomControls'

interface Props {
  sites: Site[]
  health: SiteHealth
  tamper: SiteTamper
  clusters: Cluster[]
  onWatch: (siteId: string, camera: string) => void
  onOpenIncident: (cluster: Cluster) => void
}

// MapsPage is the sites' plans as a page of their own (#282): one site at a
// time, its plan large, and its cameras listed beside it with those that need
// a look first. Before it, a plan showed only beside a camera already watched.
export function MapsPage({ sites, health, tamper, clusters, onWatch, onOpenIncident }: Props) {
  // A site whose plan could not be read is left out rather than shown broken.
  const [maps, setMaps] = useState<Record<string, SiteMap | null>>({})
  const [cameras, setCameras] = useState<Record<string, string[]>>({})
  const [chosen, setChosen] = useState<string | null>(null)
  const [picked, setPicked] = useState<{ siteId: string; camera: string } | null>(null)
  const [zoom, setZoom] = useState(1)
  const tabIds = useId()

  // Keyed on which sites, not on the list: App filters it afresh on every
  // render, and keyed on the list every alert fetched every plan again (#287).
  const siteIds = sites.map((s) => s.id).join('\n')
  useEffect(() => {
    let cancelled = false
    for (const id of siteIds ? siteIds.split('\n') : []) {
      fetchSiteMap(id)
        .then((m) => { if (!cancelled) setMaps((prev) => ({ ...prev, [id]: m })) })
        .catch(() => { if (!cancelled) setMaps((prev) => ({ ...prev, [id]: null })) })
      fetchCameras(id)
        .then((c) => { if (!cancelled) setCameras((prev) => ({ ...prev, [id]: c ?? [] })) })
        .catch(() => {})
    }
    return () => { cancelled = true }
  }, [siteIds])

  useFinishTick(clusters)

  const withPlan = sites.filter((s) => maps[s.id]?.has_plan)
  const without = sites.filter((s) => maps[s.id] && !maps[s.id]!.has_plan)
  const site = withPlan.find((s) => s.id === chosen) ?? withPlan[0]
  const map = site ? maps[site.id]! : null

  const stateIn = (siteId: string) => (camera: string): CameraState => ({
    health: health[siteId]?.[camera] ?? 'unknown',
    tamper: tamper[siteId]?.[camera],
    inProgress: clusters.some((c) => c.site_id === siteId && c.camera === camera && isInProgress(c)),
  })
  const needsLook = (s: CameraState) => s.health === 'offline' || s.health === 'degraded' || !!s.tamper
  const siteNeedsLook = (siteId: string) => (cameras[siteId] ?? []).some((c) => needsLook(stateIn(siteId)(c)))

  if (sites.length > 0 && Object.keys(maps).length < sites.length && withPlan.length === 0) {
    return <p className="px-6 py-5 text-sm text-zinc-400">Loading plans…</p>
  }
  if (sites.length > 0 && sites.every((s) => maps[s.id] === null)) {
    return <p role="alert" className="px-6 py-5 text-sm text-fault">Could not load the sites’ plans. Check the connection and reload.</p>
  }
  if (!site || !map) {
    return (
      <div className="px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-2">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-100">Maps</h1>
        <p className="text-sm text-zinc-400">No site has a plan yet. An administrator can upload one, and place its cameras, in the Overwatch Console.</p>
      </div>
    )
  }

  const stateOf = stateIn(site.id)
  const placeOf = (camera: string) => map.placements.find((p) => p.camera === camera)?.place
  const all = cameras[site.id] ?? map.placements.map((p) => p.camera)
  const needs = all.filter((c) => needsLook(stateOf(c)))
  const rest = all.filter((c) => !needsLook(stateOf(c)))
  const current = picked?.siteId === site.id ? picked.camera : null
  const pick = (camera: string) => setPicked({ siteId: site.id, camera })

  return (
    // The cameras go under the plan below a desktop's width, as an incident's
    // details do (#284).
    <div className="h-full flex flex-col lg:flex-row min-w-0 relative max-lg:overflow-y-auto">
      <div className="lg:flex-1 min-w-0 relative lg:overflow-y-auto scrollbar-thin px-4 py-4 sm:px-6 sm:py-5 flex flex-col gap-3.5">
        <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">Maps</h1>
          <div role="tablist" aria-label="Site" className="flex flex-wrap gap-0.5 p-0.5 rounded-md border border-zinc-800 bg-zinc-900">
            {withPlan.map((s) => (
              <button
                key={s.id}
                id={`${tabIds}-${s.id}`}
                type="button"
                role="tab"
                aria-selected={s.id === site.id}
                aria-controls={`${tabIds}-panel`}
                tabIndex={s.id === site.id ? 0 : -1}
                onClick={() => { setChosen(s.id); setZoom(1) }}
                onKeyDown={(e) => {
                  const next = nextTab(e.key, withPlan.map((w) => w.id), site.id)
                  if (!next) return
                  e.preventDefault()
                  setChosen(next)
                  setZoom(1)
                  document.getElementById(`${tabIds}-${next}`)?.focus()
                }}
                className={`h-8 max-sm:h-11 px-3 flex items-center gap-2 rounded text-sm transition-colors ${s.id === site.id ? 'bg-zinc-800 text-zinc-100 font-semibold' : 'text-zinc-400 hover:text-zinc-100'}`}
              >
                {s.name || s.id}
                {siteNeedsLook(s.id) && (
                  <>
                    <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-attention" />
                    <span className="sr-only">, needs a look</span>
                  </>
                )}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          {without.length > 0 && (
            <span className="text-[13px] text-zinc-400">
              {`${without.map((s) => s.name || s.id).join(', ')} ${without.length === 1 ? 'has' : 'have'} no plan yet`}
            </span>
          )}
        </div>

        <div id={`${tabIds}-panel`} role="tabpanel" aria-labelledby={`${tabIds}-${site.id}`} className="relative">
          <SitePlan
            siteId={site.id}
            siteName={site.name || site.id}
            map={map}
            stateOf={stateOf}
            current={current}
            currentIs="picked"
            onSelect={pick}
            hint={(camera) => (camera === current ? 'Picked' : 'Click to pick it')}
            labels
            zoom={zoom}
            maxHeight="max(20rem, calc(100dvh - 16rem))"
          />
          <ZoomControls zoom={zoom} onZoom={setZoom} />
        </div>

        <ul aria-label="Key" className="flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-zinc-400">
          <li className="flex items-center gap-1.5"><span aria-hidden className="w-2 h-2 rounded-full bg-live" />Incident in progress</li>
          <li className="flex items-center gap-1.5"><span aria-hidden className="w-2.5 h-2.5 rounded-full border-2 border-fault" />Offline</li>
          <li className="flex items-center gap-1.5"><span aria-hidden className="w-2.5 h-2.5 rounded-full border-2 border-dashed border-degraded" />Degraded</li>
          <li className="flex items-center gap-1.5"><span aria-hidden className="w-3 h-3 rounded-full bg-attention text-zinc-950 text-[9px] font-extrabold leading-3 text-center before:content-['!']" />View lost</li>
          <li className="flex items-center gap-1.5">
            <svg aria-hidden className="w-3 h-3" viewBox="0 0 12 12"><path d="M6 11L1 2h10z" fill="rgb(161 161 170 / 0.6)" /></svg>
            Where it faces
          </li>
        </ul>
      </div>

      <aside aria-label={current ? 'Camera' : 'Cameras'} className="w-full lg:w-[23.75rem] shrink-0 relative lg:overflow-y-auto scrollbar-thin flex flex-col gap-3.5 bg-zinc-900 border-t lg:border-t-0 lg:border-l border-zinc-800 px-[1.125rem] py-[1.125rem]">
        {current ? (
          <PickedCamera
            camera={current}
            place={placeOf(current)}
            siteName={site.name || site.id}
            state={stateOf(current)}
            latest={clusters.filter((c) => c.site_id === site.id && c.camera === current).reduce<Cluster | null>((b, c) => (!b || c.end_time > b.end_time ? c : b), null)}
            onBack={() => setPicked(null)}
            onWatch={() => onWatch(site.id, current)}
            onOpenIncident={onOpenIncident}
          />
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-base font-semibold text-zinc-100 truncate">{site.name || site.id}</h2>
              <span className="text-[13px] font-mono text-zinc-400 tabular-nums">{`${all.length} ${all.length === 1 ? 'camera' : 'cameras'}`}</span>
            </div>
            {needs.length > 0 && <CameraGroup title="Needs a look" attention cameras={needs} stateOf={stateOf} placeOf={placeOf} onPick={pick} />}
            {rest.length > 0 && <CameraGroup title="The rest" cameras={rest} stateOf={stateOf} placeOf={placeOf} onPick={pick} />}
            <p className="text-[13px] text-zinc-400">Pick a camera on the plan or here to see it, then watch it live.</p>
          </>
        )}
      </aside>
    </div>
  )
}

function CameraGroup({ title, attention = false, cameras, stateOf, placeOf, onPick }: {
  title: string
  attention?: boolean
  cameras: string[]
  stateOf: (camera: string) => CameraState
  placeOf: (camera: string) => string | undefined
  onPick: (camera: string) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className={`text-xs font-mono uppercase tracking-wider ${attention ? 'text-attention' : 'text-zinc-400'}`}>{title}</h3>
      <ul aria-label={title} className="flex flex-col gap-1.5">
        {cameras.map((camera) => {
          const state = stateOf(camera)
          const words = describe(state)
          const tone = state.health === 'offline' ? 'text-fault' : state.tamper ? 'text-attention' : state.health === 'degraded' ? 'text-degraded' : state.inProgress ? 'text-live' : 'text-zinc-400'
          return (
            <li key={camera}>
              <button type="button" onClick={() => onPick(camera)} className="w-full min-h-11 px-3 py-1.5 flex items-center justify-between gap-2.5 rounded-md border border-zinc-800 bg-zinc-950 text-left hover:bg-zinc-800">
                <span className="min-w-0 flex flex-col">
                  <span className="text-sm font-semibold text-zinc-100 truncate">{camera}</span>
                  {placeOf(camera) && <span className="text-xs text-zinc-400 truncate">{placeOf(camera)}</span>}
                </span>
                {words.length > 0 && <span className={`shrink-0 text-[13px] font-semibold ${tone}`}>{` ${words.join(', ')}`}</span>}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function PickedCamera({ camera, place, siteName, state, latest, onBack, onWatch, onOpenIncident }: {
  camera: string
  place?: string
  siteName: string
  state: CameraState
  latest: Cluster | null
  onBack: () => void
  onWatch: () => void
  onOpenIncident: (cluster: Cluster) => void
}) {
  const offline = state.health === 'offline'
  const words = describe(state)
  return (
    <section aria-label={camera} className="flex flex-col gap-3.5">
      <button type="button" onClick={onBack} className="self-start h-8 max-sm:h-11 pl-2 pr-3 flex items-center gap-1.5 rounded-md border border-zinc-800 text-sm text-zinc-100 hover:bg-zinc-800">
        <svg aria-hidden="true" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m15 18-6-6 6-6" />
        </svg>
        All cameras
      </button>
      <div className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold text-zinc-100 break-words">{nameOf(camera, undefined)}</h2>
        <p className="text-sm text-zinc-400">{place ? `${siteName} · ${place}` : siteName}</p>
      </div>
      {latest ? (
        <img src={imageUrl(latest.primary_event_id ?? latest.last_event_id, latest.end_time)} alt="" width={344} height={194} className="w-full aspect-video rounded-lg object-cover bg-zinc-950 border border-zinc-800" />
      ) : (
        <div className="w-full aspect-video rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-center text-[13px] text-zinc-400">No recent picture</div>
      )}
      <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
        <dt className="text-zinc-400">State</dt>
        <dd className={offline ? 'text-fault' : state.tamper ? 'text-attention' : 'text-zinc-100'}>{words.join(', ') || 'Not reported'}</dd>
        <dt className="text-zinc-400">Latest</dt>
        <dd className="text-zinc-100">
          {latest ? (
            <>
              <span className="capitalize">{latest.label === 'tamper' ? 'View lost' : (latest.label || 'detection').replace(/_/g, ' ')}</span>
              {' · '}
              {isInProgress(latest) ? <span className="text-live">in progress</span> : relTime(latest.end_time)}
            </>
          ) : 'Nothing recent'}
        </dd>
      </dl>
      <div className="flex flex-col gap-2">
        <button type="button" onClick={onWatch} disabled={offline} className="h-11 flex items-center justify-center gap-2 rounded-md bg-orange-400 text-zinc-950 text-sm font-bold hover:bg-orange-300 disabled:opacity-45 disabled:cursor-not-allowed">
          <svg aria-hidden="true" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.5-2.3A1 1 0 0 1 21 8.6v6.8a1 1 0 0 1-1.5.9L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z" />
          </svg>
          Watch live
        </button>
        {offline && <p className="text-[13px] text-zinc-400 text-center">Offline — nothing to watch until it is back.</p>}
        {latest && (
          <button type="button" onClick={() => onOpenIncident(latest)} className="h-10 rounded-md border border-zinc-800 bg-zinc-950 text-sm font-semibold text-zinc-100 hover:bg-zinc-800">
            Open incident
          </button>
        )}
      </div>
    </section>
  )
}
